import { uniqBy } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CancellableAPIRequest } from "restAPI";
import { getAnyaAgents } from "restAPI/AnyaAgentsAPI";
import { AnyaAgentSummary } from "views/Automations/AnyaAgents/types";

type Params = {
  autoSelectDefaultAgent?: boolean;
  enabled?: boolean;
  initialSelectedAgent?: AnyaAgentSummary | null;
  pageSize?: number;
};

const DEFAULT_AGENT_SWITCHER_PAGE_SIZE = 5;

function isAnyaAgentSummary(
  agent: AnyaAgentSummary | null | undefined,
): agent is AnyaAgentSummary {
  return Boolean(agent);
}

export default function useAnyaAgentSwitcher({
  autoSelectDefaultAgent = false,
  enabled = true,
  initialSelectedAgent = null,
  pageSize = DEFAULT_AGENT_SWITCHER_PAGE_SIZE,
}: Params = {}) {
  const [agents, setAgents] = useState<AnyaAgentSummary[]>([]);
  const [defaultAgent, setDefaultAgent] = useState<AnyaAgentSummary | null>(
    null,
  );
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState("");
  const [selectedAgent, setSelectedAgent] = useState<AnyaAgentSummary | null>(
    initialSelectedAgent,
  );
  const fetchAgentsCancelRef = useRef(() => {});

  const fetchAgents = useCallback(
    (nextSearchTerm = "") => {
      setLoading(true);
      setError("");
      const clearActiveAgentRequest = () => {
        fetchAgentsCancelRef.current();
        fetchAgentsCancelRef.current = () => {};
      };

      const { promise, cancel } = getAnyaAgents({
        page: 0,
        size: pageSize,
        search: nextSearchTerm.trim() || undefined,
      });

      clearActiveAgentRequest();

      const handledPromise = promise
        .then((response) => {
          const data = response?.data;
          const nextAgents = Array.isArray(data?.content) ? data.content : [];
          const nextDefaultAgent = data?.default_agent ?? null;

          setAgents(nextAgents);
          setDefaultAgent(nextDefaultAgent);

          return uniqBy(
            [nextDefaultAgent, ...nextAgents].filter(isAnyaAgentSummary),
            (agent) => String(agent.id),
          );
        })
        .catch((fetchError: any) => {
          setAgents([]);
          setDefaultAgent(null);
          const nextError =
            fetchError?.response?.data?.message ||
            fetchError?.message ||
            "Failed to load Anya agents. Please try again.";
          setError(nextError);
          throw new Error(nextError);
        })
        .finally(() => {
          setLoading(false);
          fetchAgentsCancelRef.current = () => {};
        });

      fetchAgentsCancelRef.current = cancel;
      return { promise: handledPromise, cancel: clearActiveAgentRequest };
    },
    [pageSize],
  );

  useEffect(() => {
    if (!enabled) {
      setAgents([]);
      setDefaultAgent(null);
      setError("");
      setLoading(false);
      return;
    }

    const { promise, cancel } = fetchAgents("");
    promise.catch(() => {
      // Errors are already handled inside fetchAgents (setError + throw).
      // This prevents an unhandled rejection from escaping the effect.
    });
    return cancel;
  }, [enabled, fetchAgents]);

  useEffect(() => {
    setSelectedAgent(initialSelectedAgent);
  }, [initialSelectedAgent]);

  useEffect(() => {
    if (autoSelectDefaultAgent && !selectedAgent && defaultAgent) {
      setSelectedAgent(defaultAgent);
    }
  }, [autoSelectDefaultAgent, defaultAgent, selectedAgent]);

  const agentOptions = useMemo(
    () =>
      uniqBy(
        [selectedAgent, defaultAgent, ...agents].filter(isAnyaAgentSummary),
        (agent) => String(agent.id),
      ),
    [agents, defaultAgent, selectedAgent],
  );

  const selectAgent = useCallback(
    (nextAgent: AnyaAgentSummary | string | number | null) => {
      if (!nextAgent) {
        setSelectedAgent(null);
        return;
      }

      if (typeof nextAgent === "object") {
        setSelectedAgent(nextAgent);
        return;
      }

      const matchedAgent = agentOptions.find(
        (agent) => String(agent.id) === String(nextAgent),
      );

      if (matchedAgent) {
        setSelectedAgent(matchedAgent);
      }
    },
    [agentOptions],
  );

  return {
    agentsLoading: loading,
    agentsError: error,
    agentOptions,
    defaultAgent,
    selectedAgent,
    setSelectedAgent,
    selectAgent,
    fetchAgents,
  };
}
